// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	ReactElement,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

// types
import {
	Account,
	Contract,
	Entity,
	ErrorCause,
	ExchangeRateByDate,
	ExpenseType,
	Feedback,
	FinancierExt,
	Invoice,
	Loan,
	PaymentPayloadUpdate,
	Transaction,
	TransactionMetadata,
} from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, InputAdornment, SelectChangeEvent } from '@mui/material';
import { LockOutlined as IconLock } from '@mui/icons-material';

// fields
import { Amounts, AmountsValue } from '../../fields/Amounts/Amounts';
import { AutocompleteTags } from '../../fields/AutocompleteTags/AutocompleteTags';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input } from '../../fields/Input/Input';
import { SelectCategory } from '../../fields/SelectCategory/SelectCategory';
import { SelectComponent } from '../../fields/SelectComponent/SelectComponent';
import { Select } from '../../fields/Select/Select';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// ui
import { BannerProps } from '../../ui/Banner/Banner';
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';
import { Loading } from '../../ui/Loading/Loading';

// utils
import { formatDate } from '../../../utils/dates';
import { postOrGetTags } from '../../../utils/tags';

// props
export interface FormEditTransactionValues {
	amounts: Record<string, number>;
	category: string;
	component: string;
	contract: string;
	date: string;
	designatedAccount: string;
	expenseType: ExpenseType | string;
	fromEntity?: string;
	invoiceAmount?: string;
	invoiceCurrency?: string;
	tags: string[];
	toEntity: string;
	total: number;
	vendor?: string;
}

export interface FormEditTransactionProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	transaction: Transaction;
}

export const FormEditTransaction = ({
	onClose,
	onSuccess,
	setIsLoading,
	transaction,
}: FormEditTransactionProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [fromAccount, setFromAccount] = useState<Account | null>(null);
	const [toAccount, setToAccount] = useState<Account | null>(null);
	const [values, setValues] = useState<FormEditTransactionValues>({
		amounts: {},
		category: '',
		component: '',
		contract: '',
		date: '',
		designatedAccount: '',
		expenseType: '',
		fromEntity: undefined,
		invoiceAmount: '',
		invoiceCurrency: '',
		tags: [],
		toEntity: '',
		total: 0,
		vendor: '',
	});

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// hooks
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// Get reporting currency
	const currencyReporting =
		project?.currencies?.find((currency) => currency.type === 'REPORTING') || null;

	// vars
	const idForm = 'form-edit-transaction';
	const supplier = transaction?.toEntity as Entity;
	const propsBanner: Partial<BannerProps> = { message: t('alert.paymentUpdated') };

	// memos
	const { isBalance, isInflow, isOpEx, isPayment, isSpecial, isReimbursement, type } =
		useMemo(() => {
			const type = transaction?.type;
			return {
				isBalance: ['INITIAL', 'REPLENISHMENT'].includes(type),
				isInflow: ['ADVANCE', 'INITIAL', 'REIMBURSEMENT', 'REPLENISHMENT'].includes(type),
				isOpEx: type === 'OPEX',
				isPayment: type === 'PAYMENT',
				isSpecial: type === 'SPECIAL',
				isReimbursement: type === 'REIMBURSEMENT',
				type,
			};
		}, [transaction]);

	const transactionCurrency = useMemo(() => {
		let currency = transaction.currency;

		if (!currency) {
			currency = transaction.payments[0]?.currency;
		}

		if (!currency) {
			if (isInflow) {
				currency = toAccount?.currency;
			} else {
				currency = fromAccount?.currency;
			}
		}

		return currency;
	}, [fromAccount, isInflow, toAccount, transaction]);

	const transactionLoans: Loan[] = useMemo(() => {
		const loans: Loan[] = [];

		// get loans from payments
		for (let i = 0; i < transaction.payments.length; i++) {
			const payment = transaction.payments[i];
			const paymentLoan = payment.loan as Loan;
			const loanFound = loans.find((l) => l.id === paymentLoan.id);

			if (!loanFound) {
				loans.push(paymentLoan);
			}
		}

		return loans;
	}, [transaction.payments]);

	// derived vars
	const fromAccountLoans: Loan[] = fromAccount ? (fromAccount.loans as Loan[]) : [];
	const fromAccountLoansGovOth = fromAccountLoans.filter((loan) =>
		financiersExt.includes(loan.financier as FinancierExt)
	);
	const hasOnlyGovOthLoans = fromAccountLoansGovOth.length === fromAccountLoans.length;
	const hasDesignatedAccount =
		fromAccount?.type === 'LOCAL' &&
		Boolean(fromAccount?.loans?.length) &&
		!hasOnlyGovOthLoans &&
		!fromAccount?.isReimbursement;

	// check if from account has disabled loans

	let hasDisabledLoans = false;

	for (let i = 0; i < fromAccountLoans.length; i++) {
		if (fromAccountLoans[i]?.status === 'DISABLED') {
			hasDisabledLoans = true;
			break;
		}
	}

	// vars
	const contract =
		contracts.find((contract) => String(contract.id) === values.contract) || undefined;
	const optionsAccountsDesignated = fromAccount
		? (fromAccount?.accounts as Account[])
				?.filter((account) => {
					return account.type === 'DESIGNATED';
				})
				.map((account) => {
					return {
						disabled: account.status === 'DEACTIVATED',
						label: <ListItemTextAccount account={account} />,
						secondary: account.institution,
						value: String(account.id),
					};
				})
		: [];

	let isContractCurrencyDifferent = false;

	if (contract && transactionCurrency) {
		isContractCurrencyDifferent = !contract.amounts.some(
			(amount) => amount.currency === transactionCurrency
		);
	}

	if (type === 'CASH_BENEFIT') {
		propsBanner.message = t('alert.cashBenefitUpdated');
	} else if (type === 'OPEX') {
		propsBanner.message = t('alert.opExUpdated');
	} else if (type === 'REIMBURSEMENT') {
		propsBanner.message = t('alert.reimbursementUpdated');
	}

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch supplier contracts
	useFetch({
		isEnabled: Boolean(supplier),
		url: `${process.env.REACT_APP_API_URL}/contracts?entities=${supplier?.id}&fields=amounts,id,num&orderBy=num&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
	});

	// fetch fromAccount
	const { isLoading: isLoadingFromAccount } = useFetch({
		isEnabled: Boolean(!isInflow && project?.id && transaction?.fromAccount),
		url: `${process.env.REACT_APP_API_URL}/accounts/${
			(transaction?.fromAccount as Account)?.id
		}?populate=accounts,balances,loans`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const account = res.data[0];
				setFromAccount(account);
			}
		},
	});

	// fetch toAccount
	const { isLoading: isLoadingToAccount } = useFetch({
		isEnabled: Boolean(isInflow && project?.id && transaction?.toAccount),
		url: `${process.env.REACT_APP_API_URL}/accounts/${
			(transaction?.toAccount as Account)?.id
		}?populate=accounts,balances,loans`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const account = res.data[0];
				setToAccount(account);
			}
		},
	});

	// fetch expense types
	let urlFetchExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&populate=documentTypes&status=ACTIVE&orderBy=name&order=ASC`;

	if (userEntity) {
		urlFetchExpenseTypes += `&entity=null,${userEntity.id}`;
	}

	useFetch({
		isEnabled: Boolean(project?.id && user),
		url: urlFetchExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
	});

	const isLoading = isLoadingFromAccount || isLoadingToAccount;

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeDesignatedAccount = async (e: SelectChangeEvent<unknown>) => {
		if (!fromAccount) return;

		const designatedAccount = (fromAccount.accounts as Account[]).find(
			(account) => String(account.id) === e.target.value
		);

		if (designatedAccount) {
			const currenciesForExchangeRates = getCurrenciesForExchangeRates({
				currency: fromAccount.currency,
				currenciesExtra: [designatedAccount.currency],
			});

			const exchangeRatesNew: ExchangeRateByDate[] = currenciesForExchangeRates.map((currency) => {
				let existingRate: { date: string; rate: number } | undefined;
				if (transaction.payments) {
					transaction.payments.forEach((payment) => {
						if (payment.conversions) {
							payment.conversions.forEach((conversion) => {
								if (conversion.currency === currency) {
									existingRate = {
										date: formatDate({
											date: new Date(conversion.dateExchangeRate),
											format: 'YYYY-MM-DD',
										}),
										rate: conversion.exchangeRate,
									};
								}
							});
						}
					});
				}

				return {
					currency,
					dateExchangeRate: existingRate?.date || '',
					exchangeRate: existingRate?.rate || 0,
				};
			});

			setExchangeRates(exchangeRatesNew);
		}

		setValues((prevState) => {
			return {
				...prevState,
				designatedAccount: String(e.target.value),
			};
		});
	};
	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const optionsContract = contracts.map((contract) => {
		// Display comma-separated list of currencies
		const currencies =
			contract.amounts.length > 0
				? contract.amounts.map((amount) => amount.currency).join(', ')
				: undefined;

		return {
			label: contract.num,
			secondary: currencies,
			value: String(contract.id),
		};
	});

	const optionsExpenseTypes = expenseTypes.map((expenseType) => {
		return {
			label: expenseType.name,
			MenuItemProps: {
				resource: expenseType,
			},
			value: String(expenseType.id),
		};
	});

	const renderAmounts = () => {
		if (transactionCurrency && transactionLoans.length) {
			return (
				<>
					<Grid item xs={12}>
						<Amounts
							currency={transactionCurrency}
							exchangeRates={exchangeRates}
							formId={idForm}
							hasCurrency={false}
							hasTax={true}
							isReadOnly={true}
							loans={transactionLoans || ([] as Loan[])}
							onChange={(e, total) => {
								// Allow Amounts component to function normally for calculations
								// The readonly nature is handled by DOM manipulation below
								const amountsNew: FormEditTransactionValues['amounts'] = { ...values.amounts };
								const indexSource = e.target.getAttribute('data-index');

								amountsNew[indexSource as string] = e.target.value as unknown as number;

								setValues((prevState) => ({
									...prevState,
									amounts: amountsNew,
									total: total,
								}));
							}}
							title={t('financingSources')}
							values={(() => {
								// Create array of value objects for each payment
								const valuesArray: AmountsValue[] = [];
								if (transaction.payments && Array.isArray(transaction.payments)) {
									transaction.payments.forEach((payment, index) => {
										valuesArray[index] = {
											amount: payment.amount || 0,
											loan: (payment.loan || {}) as Loan,
											tax: payment.tax || 0,
										};
									});
								}
								return valuesArray;
							})()}
						/>
					</Grid>
					{hasDisabledLoans && (
						<Grid item xs={12}>
							<Alert severity="info">{t('alert.financingSourcesDisabled')}</Alert>
						</Grid>
					)}
				</>
			);
		} else {
			return (
				<Grid item xs={12}>
					<Alert severity="info">{t('alert.accountNoFinancingSources')}</Alert>
				</Grid>
			);
		}
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loading
			setIsLoading(true);

			let tags: number[] = [];

			if (entries.tags && project) {
				// post or get tags
				const resTags = await postOrGetTags({
					names: (entries.tags as string).split(','),
					project: project.id,
				});

				if (resTags.length) {
					// define tags ids
					tags = resTags.map((tag) => tag.id);
				}
			}

			// Define payment conversions
			const payments: PaymentPayloadUpdate[] = [];

			if (transaction.payments) {
				const transactionPayments = transaction.payments;

				for (let s = 0; s < transactionLoans.length; s++) {
					const loan = transactionLoans[s];

					// Find the corresponding payment from the transaction
					const existingPayment = transactionPayments.find(
						(payment) => (payment.loan as Loan)?.id === loan.id || payment.loan === loan.id
					);

					if (existingPayment) {
						// Define conversions using exchange rates
						const conversions = exchangeRates.map((exchangeRate, indexExchangeRate) => {
							const conversionAmount =
								Number(entries[`source-${s}-amount-conversion-${indexExchangeRate}`]) || 0;
							const conversionTax =
								Number(entries[`source-${s}-tax-conversion-${indexExchangeRate}`]) || 0;

							return {
								amount: conversionAmount,
								currency: exchangeRate.currency,
								dateExchangeRate: exchangeRate.dateExchangeRate,
								exchangeRate: exchangeRate.exchangeRate,
								tax: conversionTax,
							};
						});

						// Only include payments that exist in the transaction
						payments.push({
							id: existingPayment.id,
							conversions: conversions.length > 0 ? conversions : [],
						});
					}
				}
			}
			const metadata: TransactionMetadata = {
				...transaction?.metadata,
			};
			if (values.invoiceAmount !== '') {
				metadata.invoiceAmount = Number(values.invoiceAmount);
			}
			if (values.invoiceCurrency) {
				metadata.invoiceCurrency = values.invoiceCurrency;
			}
			if (values.vendor) {
				metadata.vendor = values.vendor;
			}
			const body: {
				category: number;
				component: number;
				contract: number | null;
				designatedAccount: number | null;
				expenseType: number;
				metadata: TransactionMetadata;
				payments: PaymentPayloadUpdate[];
				tags: number[];
			} = {
				category: Number(entries.category),
				component: Number(entries.component),
				contract: entries.contract ? Number(entries.contract) : null,
				designatedAccount: entries.designatedAccount ? Number(entries.designatedAccount) : null,
				expenseType: Number(entries.expenseType),
				metadata,
				payments,
				tags,
			};

			const fetchEditTransaction = await fetch(
				`${process.env.REACT_APP_API_URL}/transactions/${transaction.id}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);

			const resEditTransaction = await fetchEditTransaction.json();

			if (resEditTransaction.error) {
				throw new Error(resEditTransaction.error.message, {
					cause: {
						id: resEditTransaction.error.id,
						details: resEditTransaction.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.paymentUpdated'),
				severity: 'success',
				...propsBanner,
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause.id === 'TRANSACTION_ID_NON_EXISTENT') {
				feedbackNew.alert.children = t('error.transactionIdNonExistent');
			}

			if (cause.id === 'TRANSACTION_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			// set error
			setFeedback(feedback);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// Update form values when transaction changes
	useEffect(() => {
		if (transaction) {
			const categoryValue =
				typeof transaction.category === 'object' && transaction.category?.id
					? String(transaction.category.id)
					: String(transaction.category || '');
			const componentValue =
				typeof transaction.component === 'object' && transaction.component?.id
					? String(transaction.component.id)
					: String(transaction.component || '');
			const invoice =
				typeof transaction.invoice === 'object' && transaction.invoice
					? (transaction.invoice as Invoice)
					: null;

			const invoiceAmount = invoice?.amount ?? transaction.metadata?.invoiceAmount;
			const valuesNew = {
				amounts: {},
				category: categoryValue,
				component: componentValue,
				contract: String((transaction.contract as Contract)?.id || ''),
				date: transaction.date || '',
				designatedAccount: '',
				expenseType: String((transaction.expenseType as ExpenseType)?.id || ''),
				fromEntity: transaction.fromEntity
					? String(
							typeof transaction.fromEntity === 'number'
								? transaction.fromEntity
								: transaction.fromEntity.id
						)
					: undefined,
				tags: transaction.tags ? transaction.tags.map((tag) => tag.name) : [],
				toEntity: transaction.toEntity
					? String(
							typeof transaction.toEntity === 'number'
								? transaction.toEntity
								: transaction.toEntity.id
						)
					: '',
				invoiceAmount: invoiceAmount != null ? String(invoiceAmount) : '',
				invoiceCurrency: invoice?.currency ?? transaction.metadata?.invoiceCurrency ?? '',
				total: 0,
				vendor: transaction.metadata?.vendor || '',
			};

			setValues(valuesNew);
		}
	}, [transaction]);

	// Set default exchange rates when transaction changes
	useEffect(() => {
		if (transaction) {
			// Set up exchange rates based on existing transaction data
			const exchangeRatesNew: ExchangeRateByDate[] = [];

			// Extract existing exchange rates from transaction payments
			const exchangeRatesExisting = new Map<string, { date: string; rate: number }>();

			if (transaction.payments) {
				transaction.payments.forEach((payment) => {
					if (payment.conversions) {
						payment.conversions.forEach((conversion) => {
							exchangeRatesExisting.set(conversion.currency, {
								date: formatDate({
									date: new Date(conversion.dateExchangeRate),
									format: 'YYYY-MM-DD',
								}),
								rate: conversion.exchangeRate,
							});
						});
					}
				});
			}

			// Add exchange rates for all project currencies that are different from transaction currency
			if (transactionCurrency) {
				const currenciesForExchangeRates = getCurrenciesForExchangeRates({
					currency: transactionCurrency,
				});

				currenciesForExchangeRates.forEach((currency) => {
					const existingRate = exchangeRatesExisting.get(currency);
					exchangeRatesNew.push({
						currency,
						dateExchangeRate: existingRate?.date || '',
						exchangeRate: existingRate?.rate || 0,
					});
				});
			}

			// Add exchange rate for designated account if different from sending account and not already added
			if (fromAccount) {
				const designatedAccountId =
					typeof transaction.designatedAccount === 'number'
						? transaction.designatedAccount
						: transaction.designatedAccount?.id;

				const transactionDesignatedAccount = (fromAccount?.accounts as Account[]).find(
					(acc) => acc.id === designatedAccountId
				);

				if (
					transactionDesignatedAccount &&
					transactionDesignatedAccount.currency !== fromAccount.currency
				) {
					const existingIndex = exchangeRatesNew.findIndex(
						(rate) => rate.currency === transactionDesignatedAccount.currency
					);
					if (existingIndex === -1) {
						const existingRate = exchangeRatesExisting.get(transactionDesignatedAccount.currency);

						exchangeRatesNew.push({
							currency: transactionDesignatedAccount.currency,
							dateExchangeRate: existingRate?.date || '',
							exchangeRate: existingRate?.rate || 0,
						});
					}
				}

				setValues((valuesOld) => {
					const valuesNew = { ...valuesOld };
					if (designatedAccountId) {
						valuesNew.designatedAccount = String(designatedAccountId);
					}
					return valuesNew;
				});
			}

			setExchangeRates(exchangeRatesNew);
		}
	}, [
		currencyReporting,
		fromAccount,
		getCurrenciesForExchangeRates,
		isInflow,
		project,
		toAccount,
		transaction,
		transactionCurrency,
	]);

	if (isLoading) {
		return (
			<Grid item xs={12}>
				<Grid alignItems="center" container justifyContent="center">
					<Grid item>
						<Loading />
					</Grid>
				</Grid>
			</Grid>
		);
	}

	const warnings: string[] = [];

	if (isContractCurrencyDifferent) {
		warnings.push(
			t('warning.contractCurrencyDifferent', {
				contractCurrency: contract?.amounts.map((a) => a.currency).join(', ') || t('na'),
				fromAccountCurrency: fromAccount?.currency,
			})
		);
	}

	return (
		<form id={`${idForm}`} name="formEditAccount" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{!isBalance && !isReimbursement && (
					<>
						<Grid item xs={12} md={6}>
							<SelectComponent
								id={`${idForm}-component`}
								label={t('component')}
								name="component"
								onChange={handleChangeValue}
								required={true}
								value={values.component}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<SelectCategory
								id={`${idForm}-category`}
								label={t('category')}
								name="category"
								onChange={handleChangeValue}
								required={true}
								value={values.category}
							/>
						</Grid>
						<Grid item xs={12} md={isPayment || isSpecial ? 6 : 12}>
							<Select
								id={`${idForm}-expense-type`}
								label={t('expenseType')}
								name="expenseType"
								onChange={(e, child) => {
									setValues((prevState) => {
										return {
											...prevState,
											expenseType: (child as ReactElement).props.resource as ExpenseType,
										};
									});
								}}
								options={optionsExpenseTypes}
								required={true}
								value={
									typeof values.expenseType === 'object' && values.expenseType?.id
										? String(values.expenseType.id)
										: String(values.expenseType || '')
								}
							/>
						</Grid>
						{isOpEx && (
							<Grid item xs={12}>
								<Input
									id={`${idForm}-vendor`}
									label={t('vendor')}
									name="vendor"
									onChange={handleChangeValue}
									value={values.vendor || ''}
								/>
							</Grid>
						)}
						{(isPayment || isSpecial) && (
							<>
								<Grid item xs={12} md={6}>
									<Select
										id={`${idForm}-contract`}
										label={t('contract')}
										name="contract"
										onChange={handleChangeValue}
										options={optionsContract}
										value={values.contract}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<Input
										id={`${idForm}-invoice-amount`}
										label={t('invoiceAmount')}
										name="invoiceAmount"
										onChange={handleChangeValue}
										required={false}
										type="number"
										value={values.invoiceAmount || ''}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<SelectCurrency
										id={`${idForm}-invoice-currency`}
										label={t('invoiceCurrency')}
										name="invoiceCurrency"
										onChange={handleChangeValue}
										value={values.invoiceCurrency}
									/>
								</Grid>
							</>
						)}
					</>
				)}
				{fromAccount && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-from-account`}
							InputProps={{
								readOnly: true,
								endAdornment: (
									<InputAdornment position="end">
										<IconLock />
									</InputAdornment>
								),
							}}
							label={t('fromAccount')}
							name="fromAccount"
							required={true}
							value={fromAccount?.name}
						/>
					</Grid>
				)}
				{hasDesignatedAccount && (
					<Grid item xs={12}>
						<Select
							helperText={t('formAddTransaction.designatedAccount.helper')}
							id={`${idForm}-designated-account`}
							label={t('designatedAccount')}
							name="designatedAccount"
							onChange={handleChangeDesignatedAccount}
							options={optionsAccountsDesignated}
							required={true}
							value={values.designatedAccount}
						/>
					</Grid>
				)}
				{exchangeRates.length > 0 && transactionCurrency && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={transactionCurrency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				{renderAmounts()}
				{userProjectPermissions?.tag?.create && (
					<Grid item xs={12}>
						<AutocompleteTags
							id={`${idForm}-tags`}
							onChange={(tags) => {
								setValues((prevState) => ({
									...prevState,
									tags,
								}));
							}}
							value={values.tags}
						/>
					</Grid>
				)}
				{warnings.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<AlertTitle>{t('warnings')}</AlertTitle>
							<ul>
								{warnings.map((warning, index) => (
									<li key={index}>{warning}</li>
								))}
							</ul>
						</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
